/**
 * Adapts live `useShanksChat` messages (ShanksMessage[]) into the ChatMessage[]
 * shape ChatThread renders, preserving the ordered interleaving of reasoning,
 * text, and tool-call parts. Shared by the proposal AI sidebar and the
 * Artifact Studio Copilot sidecar so both render shanks streams identically
 * (including the write-to-document tool card).
 */ export function convertShanksToChatMessages(shanksMessages) {
    // Build a lookup of tool call data by ID for resolving tool_call parts.
    const buildToolCallLookup = (msg)=>{
        const lookup = new Map();
        for (const tc of msg.toolCalls || []){
            lookup.set(tc.id, tc);
        }
        return lookup;
    };
    const toolCallState = (tc)=>{
        if (tc.result || tc.error) return 'output-available';
        if (tc.arguments) return 'input-available';
        return 'input-streaming';
    };
    const toolCallOutput = (tc)=>{
        if (tc.error) return {
            error: tc.error
        };
        if (tc.result) {
            try {
                return JSON.parse(tc.result);
            } catch  {
                return tc.result;
            }
        }
        return undefined;
    };
    const toolCallInput = (tc)=>{
        if (!tc.arguments) return undefined;
        try {
            return JSON.parse(tc.arguments);
        } catch  {
            return undefined;
        }
    };
    return shanksMessages.map((msg)=>{
        const parts = [];
        const toolCallLookup = buildToolCallLookup(msg);
        const renderedToolIds = new Set();
        // If the message has ordered parts, use them to preserve interleaving.
        if (msg.parts && msg.parts.length > 0) {
            for (const p of msg.parts){
                if (p.type === 'reasoning') {
                    parts.push({
                        type: 'reasoning',
                        text: p.content,
                        state: 'done'
                    });
                } else if (p.type === 'text') {
                    parts.push({
                        type: 'text',
                        text: p.content
                    });
                } else if (p.type === 'tool_call' && p.toolCallId) {
                    const tc = toolCallLookup.get(p.toolCallId);
                    if (tc) {
                        renderedToolIds.add(tc.id);
                        parts.push({
                            type: `tool-${tc.name}`,
                            toolName: tc.name,
                            toolCallId: tc.id,
                            state: toolCallState(tc),
                            output: toolCallOutput(tc),
                            input: toolCallInput(tc),
                            purpose: tc.purpose,
                            executionState: tc.executionState,
                            duration: tc.duration
                        });
                    }
                }
            }
        } else if (msg.content) {
            parts.push({
                type: 'text',
                text: msg.content
            });
        }
        // Append any tool calls that weren't in the ordered parts (fallback).
        for (const tc of msg.toolCalls || []){
            if (renderedToolIds.has(tc.id)) continue;
            parts.push({
                type: `tool-${tc.name}`,
                toolName: tc.name,
                toolCallId: tc.id,
                state: toolCallState(tc),
                output: toolCallOutput(tc),
                input: toolCallInput(tc),
                purpose: tc.purpose,
                executionState: tc.executionState,
                duration: tc.duration
            });
        }
        if (msg.provenance) {
            parts.push({
                type: 'data-provenance',
                data: msg.provenance
            });
        }
        return {
            id: msg.id,
            role: msg.role,
            parts,
            metadata: {
                createdAt: new Date().toISOString()
            }
        };
    });
}
/** Merge persisted history with live rows using server-provided message identities. */ export function mergeMessagesById(initial, live, preferLive = false) {
    const liveById = new Map(live.map((message)=>[
            message.id,
            message
        ]));
    const seen = new Set();
    const merged = [];
    for (const message of [
        ...initial,
        ...live
    ]){
        const id = message.id;
        if (id) {
            if (seen.has(id)) continue;
            seen.add(id);
        }
        merged.push(preferLive && id ? liveById.get(id) ?? message : message);
    }
    return merged;
}
